import _ from 'lodash'
import Backbone from 'backbone'
import template from './dataset-permissions-form.hbs'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import i18n from 'scoreboard/components/i18n'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import ConditionalCheckboxToggle from 'scoreboard/components/conditional-checkbox-toggle'
import groupType from 'scoreboard/admin/principals/groups/group-type'

var DatasetPermissionsForm = Backbone.View.extend({

	/**
	 * The dataset permissions dialog which is accessible from Datasets > Edit and
	 * Admin > Groups > Datasets.
	 *
	 * @constructs
	 *
	 * @param options
	 *
	 * @param {Dataset} options.model the Dataset
	 *
	 * @param {Object} options.permissions an object containing the filters and
	 * all the boolean permissions, e.g. { id: 1, filters: [], administer: true,
	 * modifyPermissions: true, modifyViews: false, modifyRecords: true }
	 *
	 * @param {Object} options.treeItemsById a hash of tree items keyed by ID
	 */
	initialize: function(options) {
		this.options = options || {}
		this.filters = _.get(options, 'permissions.filters') || []
		// #14096 work with our own copy of the filters in case the user cancels
		this.filters = _.cloneDeep(this.filters)

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			guard: false
		})
	},

	tagName: 'form',

	className: 'dataset-permissions-form',

	events: {
		'click .add-filter': 'addFilter',
		'change [name=administer]': 'onChangeAdminister',
		'change [name=modifyRecords]': 'onChangeModifyRecords',
		'change [name=viewRecordsTab]': 'onChangeViewRecordsTab',
	},

	/** @type {Dataset} */
	model: null,

	render: function() {
		this.$el.empty().append(template(this.context()))
			.addClass(this.options.groupType)	//#34574
		formHelper.writeToDom(this.options.permissions, this.el)

		// render filterBadges
		this.filterBadges = new DatasetsFilterBadges({
			model: this.model,
			filters: this.filters,
			treeItemsById: this.options.treeItemsById,
			noneMessage: i18n.get('datasets.permissions.noFilterLabel'),
			showPermissionFilters: true,
			includeEditFields: false,
			filterTextOptions: {
				showBuiltInRollupTreeGroupLabel: true
			}
		})
		this.filterBadges.on('add-filter edit-filter delete-filter',
			this.formView.change.bind(this.formView))
		this.$('.restrictive-filters .panel-content').append(
			this.filterBadges.el)
		this.filterBadges.render()

		this.secure()

		this.setupDatasetToggles()
		this.toggleDatasetToggles()

		this.formView.start()

		return this
	},

	secure: function() {
		let principal = this.options?.permissions?.principal
		let readOnly = principal?.readOnly
		let gt = this.options?.groupType ?? principal?.groupType
		if (readOnly || groupType.isReadOnly(gt)) {
			this.hideCheckboxes(this.$('.panel-content li').not('.read-only-option'))
		}
		// #34574 only show permissions available for updaters groups
		if (gt === 'updaters') {
			this.hideCheckboxes(this.$('.panel-content li').not('.updater-option'))
		}
	},

	/**
	 * Applies the is-not-displayed class to the given elements, and also
	 * unchecks any checkboxes within the elements.
	 * @param {JQuery} els the elements to hide
	 */
	hideCheckboxes: function(els) {
		els
			.addClass('is-not-displayed')
			.find('input')
				.prop('checked', false)
	},

	context: function() {
		return {
			permissionsLabel: this.options.permissionsLabel,
			permissionsGroupType: this.options.permissionsGroupType
		}
	},

	addFilter: function() {
		this.filterBadges.addFilter()
	},

	setupDatasetToggles: function () {
		this.datasetToggles = new ConditionalCheckboxToggle({el: this.$('.actions-panel')})
		this.datasetToggles.addRelation('administer', this.$('[type=checkbox]:not([name=administer])'))
		this.datasetToggles.addRelation('modifyRecords', this.$('[name=viewRecords]'))
		this.datasetToggles.addRelation('viewRecordsTab', this.$('[name=viewRecords]'))
	},

	// #34573 toggle initial enabled/disabled states for this.datasetToggles checkboxes
	toggleDatasetToggles: function() {
		this.onChangeAdminister()
		this.onChangeModifyRecords()
		this.onChangeViewRecordsTab()
	},

	onChangeAdminister: function() {
		this.datasetToggles.toggle('administer')
		// also toggle modifyRecords to ensure proper enabled/disabled state
		// of 'viewRecords' sub-checkbox
		this.datasetToggles.toggle('modifyRecords')
	},

	onChangeModifyRecords: function() {
		this.datasetToggles.toggle('modifyRecords')

		// ensure that the 'viewRecords' checkbox is disabled
		// if 'viewRecordsTab' is checked
		if (this.$('[name=viewRecordsTab]').prop('checked')) {
			this.$('[name=viewRecords]').prop('disabled', true)
		}
	},

	onChangeViewRecordsTab: function() {
		this.datasetToggles.toggle('viewRecordsTab')

		// ensure that the 'viewRecords' checkbox is disabled
		// if 'modifyRecords' is checked
		if (this.$('[name=modifyRecords]').prop('checked')) {
			this.$('[name=viewRecords]').prop('disabled', true)
		}
	},

	submit: function() {
		var data = formHelper.readFromDom(this.el)
		data.id = _.get(this.options, 'permissions.id')
		data.filters = this.filterBadges.filters
		data.principal = _.get(this.options, 'permissions.principal')
		this.trigger('done', data)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.filterBadges, this.tooltip,
			this.datasetToggles], 'remove')
	}

})

export default DatasetPermissionsForm

DatasetPermissionsForm.__filename__ = 'datasets/dataset-permissions-form.js'
